import { GoogleMedia } from '@/model/googlemedia.model';
import { MediaProgressBar } from '@/model/mediaprogressbar.model';
import { Playlist } from '@/model/playlist.model';
import { Release } from '@/model/release.model';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { BackgroundTask } from '@capawesome/capacitor-background-task';
import { isPlatform } from '@ionic/vue';
import UnicodeUtils from './unicode.utils';

import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import dayjs from 'dayjs';
// @ts-ignore
import { MittEmitter } from 'mitt-emitter';

interface DownloadTask {
  id: string;
  fileId: string;
  fileDir: string;
  mimeType?: string;
  retryCount: number;
  totalSize?: number;
  downloadedSize: number;
}

export default class FilesystemCustomUtils {
  private programName?: string;
  private programShortName?: string;
  private release?: string;
  private releaseWithZero?: string;
  private releaseMusicDirectory?: string;
  private releaseVideoDirectory?: string;
  private releaseGuideDirectory?: string;
  private releaseId?: number;
  private contentSource?: string;
  private entityProgressBar?: MediaProgressBar;
  private playlist?: Playlist;
  private emitter?: MittEmitter;
  private activeBackgroundTasks: Set<string> = new Set();
  private keepAliveInterval?: ReturnType<typeof setInterval>;
  private backgroundTaskSupported = true;

  // Download queue management
  private downloadQueue: DownloadTask[] = [];
  private currentDownloads: Map<string, DownloadTask> = new Map();
  private maxConcurrentDownloads = 1; // Process video downloads sequentially
  private isProcessingQueue = false;
  private cancelledDownloads: Set<string> = new Set(); // Track cancelled downloads

  public mimeExtensions: Map<string, string> = new Map([
    ['image/jpeg', 'jpg'],
    ['image/png', 'png'],
    ['application/json', 'json'],
    ['audio/mpeg', 'mp3'],
    ['video/mp4', 'mp4'],
    ['video/mpeg', 'mpeg'],
    ['video/quicktime', 'mov'],
    ['application/pdf', 'pdf'],
  ]);

  public PLAYLISTS_DIRECTORY = 'playlists';
  public PROGRAMS_PATH = 'programs.json';
  public PLAYLIST_PATH = 'playlist.json';
  public GUIDE_LINKS_PATH = 'guide-links.json';

  private readonly CHUNK_SIZE = 1024 * 1024 * 5; // 5MB chunks
  private readonly MAX_RETRIES = 3;
  private readonly HTTP_TIMEOUT = 120000; // 2 minutes

  private unicodeUtils = new UnicodeUtils();

  constructor() {
  }

  setRelease(programName: string, programShortName: string, release: string, releaseId?: number, contentSource?: string) {
    this.programName = programName;
    this.programShortName = programShortName;
    this.release = release;
    this.releaseId = releaseId;
    this.contentSource = contentSource || 'GOOGLE';
    this.releaseWithZero = this.release && this.release.length === 1 ? '0' + this.release : this.release;
    this.releaseMusicDirectory = `${this.programShortName + '_' + this.releaseWithZero + '_Music'}`;
    this.releaseVideoDirectory = `${this.programShortName + '_' + this.releaseWithZero + '_Video'}`;
    this.releaseGuideDirectory = `${this.programShortName + '_' + this.releaseWithZero + '_Guide'}`;
  }

  setProgressBar(entityProgressBar: MediaProgressBar) {
    this.entityProgressBar = entityProgressBar;
  }

  setEmitter(emitter: MittEmitter) {
    this.emitter = emitter;
  }

  async controlBGMode(enabled: boolean, taskId?: string) {
    try {
      // Skip background task control on web platform
      if (isPlatform('mobileweb') || isPlatform('desktop')) {
        return true;
      }

      // Try to use background task, but fall back gracefully if not supported
      if (enabled) {
        if (taskId && !this.activeBackgroundTasks.has(taskId)) {
          this.activeBackgroundTasks.add(taskId);
          try {
          await BackgroundTask.beforeExit(async () => {
            if (this.activeBackgroundTasks.has(taskId)) {
              this.activeBackgroundTasks.delete(taskId);
            }
            BackgroundTask.finish({ taskId });
          });
            this.backgroundTaskSupported = true;
            
            // Start keep-alive mechanism as fallback for Android devices
            this.startKeepAlive();
          } catch (bgError) {
            // Background task not supported on this device - continue anyway
            console.warn("Background task not supported, downloads will continue:", bgError);
            this.backgroundTaskSupported = false;
            
            // Start aggressive keep-alive for devices without background support
            this.startKeepAlive(true);
            return false;
          }
        }
      } else if (taskId) {
        if (this.activeBackgroundTasks.has(taskId)) {
          this.activeBackgroundTasks.delete(taskId);
        }
        try {
        BackgroundTask.finish({ taskId });
        } catch (bgError) {
          // Ignore finish errors - task may not have been started
          console.debug("Background task finish error (non-critical):", bgError);
        }
        
        // Stop keep-alive when no more active tasks
        if (this.activeBackgroundTasks.size === 0) {
          this.stopKeepAlive();
        }
      }
      return true;
    } catch (error) {
      console.error("Error controlling BG mode (will continue without background support):", error);
      this.backgroundTaskSupported = false;
      this.startKeepAlive(true);
      return false;
    }
  }

  private startKeepAlive(aggressive = false) {
    // Clear any existing interval
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }

    // Use aggressive mode (every 500ms) if background tasks aren't supported
    // or normal mode (every 5s) as a safety net
    const interval = aggressive ? 500 : 5000;

    this.keepAliveInterval = setInterval(() => {
      // Keep the app awake by doing a minimal operation
      // This prevents Android from killing the app during downloads
      if (this.activeBackgroundTasks.size > 0) {
        console.debug(`Keep-alive ping: ${this.activeBackgroundTasks.size} active tasks`);
      }
    }, interval);
  }

  private stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = undefined;
    }
  }

  generateTaskId(prefix = 'task'): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private isBase64(str: string): boolean {
    try {
      return btoa(atob(str)) === str;
    } catch (err) {
      return false;
    }
  }

  /**
   * Download Queue Management
   */
  private async addToDownloadQueue(fileId: string, fileDir: string, mimeType?: string): Promise<void> {
    const task: DownloadTask = {
      id: this.generateTaskId(`file-${fileId}`),
      fileId,
      fileDir,
      mimeType,
      retryCount: 0,
      downloadedSize: 0
    };

    this.downloadQueue.push(task);
    
    if (!this.isProcessingQueue) {
      this.processDownloadQueue();
    }
  }

  private async processDownloadQueue() {
    if (this.isProcessingQueue) return;
    
    this.isProcessingQueue = true;

    while (
      this.downloadQueue.length > 0 && 
      this.currentDownloads.size < this.maxConcurrentDownloads &&
      (!this.entityProgressBar || this.entityProgressBar.downloading) // Stop if download cancelled
    ) {
      const task = this.downloadQueue.shift();
      if (task) {
        // Check if task was cancelled before starting
        if (this.cancelledDownloads.has(task.fileId)) {
          console.log(`Skipping cancelled task ${task.fileId}`);
          this.cancelledDownloads.delete(task.fileId);
          continue;
        }
        
        this.currentDownloads.set(task.id, task);
        this.processDownloadTask(task).finally(() => {
          this.currentDownloads.delete(task.id);
          // Continue processing queue only if still downloading
          if (!this.entityProgressBar || this.entityProgressBar.downloading) {
            setTimeout(() => this.processDownloadQueue(), 100);
          }
        });
      }
    }

    this.isProcessingQueue = false;
  }

  private async processDownloadTask(task: DownloadTask): Promise<void> {
    try {
      await this.downloadFileChunked(task.fileId, task.fileDir, task.mimeType, 0, 0, task);
    } catch (error) {
      console.error(`Failed to download file ${task.fileId}:`, error);
      
      if (task.retryCount < this.MAX_RETRIES) {
        task.retryCount++;
        console.log(`Retrying download for ${task.fileId}, attempt ${task.retryCount}`);
        // Add back to queue for retry
        this.downloadQueue.unshift(task);
      } else {
        console.error(`Max retries exceeded for file ${task.fileId}`);
      }
    }
  }

  async downloadFileChunked(
      fileId: string, 
      fileDir: string, 
      mimeType?: string, 
      startByte?: number, 
      retryCount?: number,
      task?: DownloadTask
  ): Promise<void> {
      // Check if download was cancelled
      if (this.cancelledDownloads.has(fileId)) {
          console.log(`Download cancelled for ${fileId}`);
          this.cancelledDownloads.delete(fileId);
          return;
      }

      // Check if progress bar downloading flag is false (user stopped download)
      if (this.entityProgressBar && !this.entityProgressBar.downloading) {
          console.log(`Download stopped by user for ${fileId}`);
          return;
      }

      const taskId = task?.id || this.generateTaskId(`download-${fileId}`);
      await this.controlBGMode(true, taskId);

      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;

      if (isPlatform('ios') || isPlatform('android')) {
          const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
          const endByte = (startByte || 0) + CHUNK_SIZE - 1;

          console.log(`Downloading chunk: fileId=${fileId}, bytes=${startByte || 0}-${endByte}, retry=${retryCount || 0}`);

          try {
              const headers: any = { 
                  Authorization: `Bearer ${token}`,
                  'Cache-Control': 'no-cache'
              };

              // Always use Range header for chunked downloads
              headers['Range'] = `bytes=${startByte || 0}-${endByte}`;

              // Determine download URL based on file source
              let downloadUrl: string;
              const isReleaseServerFile = fileId.startsWith('releases/');
              const isLibraryMusicFile = fileId.startsWith('library_music/');

              if (isReleaseServerFile) {
                // Use server chunked download API for offline downloads
                downloadUrl = `${process.env.VUE_APP_API_URL}api/releases/download/base64?path=${encodeURIComponent(fileId)}`;
              } else if (isLibraryMusicFile) {
                downloadUrl = `${process.env.VUE_APP_API_URL}api/library-music/download/base64?path=${encodeURIComponent(fileId)}`;
              } else {
                // Use Google Drive API (original)
                downloadUrl = `${process.env.VUE_APP_API_URL}api/google/file/base64/id/${fileId}`;
              }

              const response = await CapacitorHttp.get({
                  url: downloadUrl,
                  headers: headers,
                  readTimeout: this.HTTP_TIMEOUT,
                  // On Android, we need the data as a string (base64)
                  // The API already returns base64, so CapacitorHttp should handle it properly
              });

              // CapacitorHttp may normalize headers to lowercase, so check both
              const getHeader = (name: string) => {
                return response.headers[name] || response.headers[name.toLowerCase()] || '';
              };
              
              const xFileComplete = getHeader('X-File-Complete');
              const xChunkEnd = getHeader('X-Chunk-End');
              const xTotalSize = getHeader('X-Total-Size');
              const xChunkSize = getHeader('X-Chunk-Size');
              
              console.log(`Download response: fileId=${fileId}, status=${response.status}, X-File-Complete=${xFileComplete}, X-Chunk-End=${xChunkEnd}, X-Total-Size=${xTotalSize}`);

              // Check again after network call if download was cancelled
              if (this.cancelledDownloads.has(fileId) || (this.entityProgressBar && !this.entityProgressBar.downloading)) {
                  console.log(`Download cancelled during request for ${fileId}`);
                  this.cancelledDownloads.delete(fileId);
                  await this.controlBGMode(false, taskId);
                  return;
              }

              // Handle different response statuses
              if (response.status === 206 || response.status === 200) {
                  // Check if download is complete FIRST (before processing data)
                  const isLastChunk = xFileComplete === 'true';
                  
                  // Ensure we have the data as a string (base64)
                  // On Android, CapacitorHttp might return data in different formats depending on Content-Type
                  let base64Data: string;
                  if (typeof response.data === 'string') {
                      base64Data = response.data;
                  } else if (response.data && typeof response.data === 'object') {
                      // If it's an object or array buffer, try to convert it
                      try {
                          base64Data = JSON.stringify(response.data);
                      } catch (e) {
                          console.error('Failed to convert response data to string', e);
                          throw new Error('Invalid response data format');
                      }
                  } else {
                      console.error('Unexpected response data type:', typeof response.data);
                      throw new Error('Invalid response data type');
                  }
                  
                  // Validate response data
                  if (!base64Data || base64Data.length === 0) {
                      if (isLastChunk) {
                          // This is a completion signal for the last chunk with no more data
                          console.log(`File download completed (empty final chunk) for ${fileId}`);
                          await this.finalizeDownload(fileId, fileDir, mimeType, taskId);
                          return;
                      }
                      throw new Error('Empty response data received');
                  }

                  await this.appendFile(base64Data, `dwn_${fileId}`, fileDir, mimeType);
                  
                  // Update progress tracking
                  const contentLength = Number(xChunkSize || response.data.length || 0);
                  const downloadedMB = contentLength / (1024 * 1024);
                  
                  if (task) {
                      task.downloadedSize += downloadedMB;
                  }

                  if (this.entityProgressBar && this.entityProgressBar.downloading) {
                      this.entityProgressBar.downloaded += downloadedMB;
                      
                      // Get total size from headers for progress calculation
                      const totalSize = Number(xTotalSize || 0) / (1024 * 1024);

                      // Update progress with timing information
                      this.entityProgressBar.updateProgress(this.entityProgressBar.downloaded, totalSize);
                  }
                  
                  // If this was the last chunk, finalize and return
                  if (isLastChunk) {
                      console.log(`File download completed (last chunk) for ${fileId}`);
                      await this.finalizeDownload(fileId, fileDir, mimeType, taskId);
                      return;
                  }

                  // Calculate next chunk position from the chunk end header
                  const chunkEnd = Number(xChunkEnd || 0);
                  
                  if (chunkEnd === 0 && (startByte || 0) === 0) {
                      // First chunk but no X-Chunk-End header - likely an error
                      console.error(`No X-Chunk-End header for ${fileId}, headers:`, response.headers);
                      await this.controlBGMode(false, taskId);
                      throw new Error('Missing X-Chunk-End header from server');
                  }
                  
                  const nextStartByte = chunkEnd + 1;
                  console.log(`Next chunk will start at byte ${nextStartByte} for ${fileId}`);

                  // Continue with next chunk only if not cancelled and downloading
                  if (!this.cancelledDownloads.has(fileId) && (!this.entityProgressBar || this.entityProgressBar.downloading)) {
                      await this.downloadFileChunked(fileId, fileDir, mimeType, nextStartByte, 0, task);
                  } else {
                      console.log(`Not continuing to next chunk for ${fileId}: cancelled=${this.cancelledDownloads.has(fileId)}, downloading=${this.entityProgressBar?.downloading}`);
                      await this.controlBGMode(false, taskId);
                  }
              } else {
                  throw new Error(`Unexpected status code: ${response.status}`);
              }
          } catch (err) {
              console.error(`Download error for file ${fileId}, bytes ${startByte}-${endByte}:`, err);
              
              // Don't retry if cancelled
              if (this.cancelledDownloads.has(fileId) || (this.entityProgressBar && !this.entityProgressBar.downloading)) {
                  console.log(`Download cancelled, not retrying ${fileId}`);
                  this.cancelledDownloads.delete(fileId);
                  await this.controlBGMode(false, taskId);
                  return;
              }
              
              if ((retryCount || 0) < this.MAX_RETRIES) {
                  console.log(`Retrying chunk for file ${fileId}, attempt ${(retryCount || 0) + 1}`);
                  await new Promise(resolve => setTimeout(resolve, 2000 * ((retryCount || 0) + 1)));
                  return this.downloadFileChunked(fileId, fileDir, mimeType, startByte, (retryCount || 0) + 1, task);
              } else {
                  console.error(`Max retries exceeded for file ${fileId}`);
                  await this.controlBGMode(false, taskId);
                  throw err;
              }
          }
      }
  }

  private async finalizeDownload(fileId: string, fileDir: string, mimeType?: string, taskId?: string) {
    console.log(`Finalizing download for file ${fileId}`);
    
    const tempFileName = `dwn_${fileId}`;
    const fileExtension = mimeType ? '.' + this.mimeExtensions.get(mimeType) : '';
    
    try {
        // Сначала проверяем, что исходный файл существует
        const finalFileName = fileId;
        
        // Проверяем существование временного файла
        const tempFileExists = await this.checkFileExists(`${fileDir}/${tempFileName}${fileExtension}`);
        
        if (!tempFileExists) {
            throw new Error(`Temporary file ${tempFileName}${fileExtension} not found in directory ${fileDir}`);
        }

        // Проверяем существование целевой директории
        const dirExists = await this.checkDirectoryExists(fileDir);
        if (!dirExists) {
            await Filesystem.mkdir({
                path: fileDir,
                directory: Directory.Data,
                recursive: true
            });
        }

        // Переименовываем файл
        await this.renameFile(tempFileName, finalFileName, fileDir, mimeType);
        
        // Проверяем целостность файла
        const fileValid = await this.validateDownloadedFile(`${fileDir}/${finalFileName}${fileExtension}`);
        
        if (!fileValid) {
            // Clean up the invalid file
            try {
                await Filesystem.deleteFile({
                    path: `${fileDir}/${finalFileName}${fileExtension}`,
                    directory: Directory.Data
                });
            } catch (cleanupError) {
                console.error('Failed to cleanup invalid file', cleanupError);
            }
            throw new Error(`Downloaded file ${fileId} is invalid`);
        }

        if (this.entityProgressBar) {
            this.entityProgressBar.currentValue += 1;
            console.log(`Download progress: ${this.entityProgressBar.currentValue}/${this.entityProgressBar.maxValue}`);
            
            if (this.entityProgressBar.currentValue >= this.entityProgressBar.maxValue) {
                this.entityProgressBar.currentValue = this.entityProgressBar.maxValue;
                await this.finalizePlaylistDownload();
            }
        }

        await this.controlBGMode(false, taskId);
    } catch (error) {
        console.error(`Error finalizing download for ${fileId}:`, error);
        
        // Clean up temporary file on error
        try {
            const tempFileExists = await this.checkFileExists(`${fileDir}/${tempFileName}${fileExtension}`);
            if (tempFileExists) {
                await Filesystem.deleteFile({
                    path: `${fileDir}/${tempFileName}${fileExtension}`,
                    directory: Directory.Data
                });
                console.log(`Cleaned up temporary file ${tempFileName}${fileExtension}`);
            }
        } catch (cleanupError) {
            console.error('Failed to cleanup temporary file', cleanupError);
        }
        
        await this.controlBGMode(false, taskId);
        throw error;
    }
}

private async checkFileExists(filePath: string): Promise<boolean> {
    try {
        await Filesystem.stat({
            path: filePath,
            directory: Directory.Data,
        });
        return true;
    } catch (error) {
        console.log(`File not found: ${filePath}`, error);
        return false;
    }
}

private async checkDirectoryExists(dirPath: string): Promise<boolean> {
    try {
        await Filesystem.readdir({
            path: dirPath,
            directory: Directory.Data,
        });
        return true;
    } catch (error) {
        console.log(`Directory not found: ${dirPath}`, error);
        return false;
    }
}

  private async finalizePlaylistDownload() {
    if (typeof this.playlist !== 'undefined') {
      // Проверяем целостность всех файлов перед сохранением плейлиста
      const allFilesValid = await this.verifyDownloadedFiles();
      
      if (allFilesValid) {
        this.playlist.offline = true;
        this.playlist.offlineDate = `${dayjs().date()}.${String(dayjs().month() + 1).padStart(2, '0')}.${dayjs().year()}`;
        
        if (typeof this.emitter !== 'undefined' && this.entityProgressBar) {
          // Save playlist file first, then emit event
          const targetDir = this.getTargetDirectory();
          const playlistFileName = process.env.VUE_APP_OFFLINE_PLAYLIST_NAME || this.PLAYLIST_PATH;
          
          await this.storeFile(
            this.unicodeUtils.toBase64(JSON.stringify(this.playlist)),
            playlistFileName,
            targetDir
          );

          // Verify that playlist file was saved successfully
          try {
            const dirContent = await Filesystem.readdir({
              path: targetDir,
              directory: Directory.Data,
            });
            
            const playlistFile = dirContent.files.find(f => 
              f.name === playlistFileName || f.name === this.PLAYLIST_PATH
            );
            
            if (playlistFile && playlistFile.size > 0) {
              // Playlist saved successfully, now emit event
              this.emitter.emit(`downloaded-${this.entityProgressBar.id}`, true);
            } else {
              console.error('Playlist file not found after save');
            }
          } catch (error) {
            console.error('Error verifying playlist save:', error);
            // Emit event anyway
            this.emitter.emit(`downloaded-${this.entityProgressBar.id}`, true);
          }

          // Sync with API (non-blocking)
          const tokenQuery = await Preferences.get({ key: 'jwt' });
          const token = tokenQuery.value;

          if (token) {
            CapacitorHttp.put({
              url: `${process.env.VUE_APP_API_URL}api/playlist`,
              headers: { 
                Authorization: `Bearer ${token}`, 
                "Content-Type": "application/json",
              },
              data: {
                name: this.playlist.id,
                content: JSON.stringify(this.playlist),
              },
            }).catch((error) => {
              console.warn('Failed to sync playlist with API (non-critical):', error);
            });
          }
        }
      } else {
        console.error('Not all files were downloaded successfully');
        // Clean up partial files if download failed
        if (this.playlist && this.playlist.id) {
          await this.cleanupPartialDownloads(this.playlist);
        }
      }
    }
    this.clearProgressBar();
  }

  private async verifyDownloadedFiles(): Promise<boolean> {
    if (!this.playlist) return false;

    try {
      const targetDir = this.getTargetDirectory();
      const files = await Filesystem.readdir({
        path: targetDir,
        directory: Directory.Data,
      });

      for (const track of this.playlist.tracks) {
        const expectedFileName = track.id + (track.mimeType ? '.' + this.mimeExtensions.get(track.mimeType) : '');
        // Only check for final files, ignore partial downloads
        const fileExists = files.files.some(f => f.name === expectedFileName && !f.name.startsWith('dwn_'));
        
        if (!fileExists) {
          console.warn(`File ${expectedFileName} not found in downloaded files`);
          return false;
        }

        // Проверяем размер файла
        const fileInfo = files.files.find(f => f.name === expectedFileName && !f.name.startsWith('dwn_'));
        if (fileInfo && fileInfo.size === 0) {
          console.warn(`File ${expectedFileName} has zero size`);
          return false;
        }

        // Дополнительная проверка целостности
        const fileValid = await this.validateDownloadedFile(`${targetDir}/${expectedFileName}`);
        if (!fileValid) {
          console.warn(`File ${expectedFileName} failed validation`);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error verifying downloaded files:', error);
      return false;
    }
  }

  private async validateDownloadedFile(filePath: string): Promise<boolean> {
    try {
      const fileInfo = await Filesystem.stat({
        path: filePath,
        directory: Directory.Data,
      });
      
      return fileInfo.size > 0;
    } catch (error) {
      console.error(`Error validating file ${filePath}:`, error);
      return false;
    }
  }

  private getTargetDirectory(): string {
    if (this.playlist?.isRelease) {
      return `${this.PLAYLISTS_DIRECTORY}/${this.releaseMusicDirectory}` || '';
    }
    return `${this.PLAYLISTS_DIRECTORY}/${this.playlist?.id}` || '';
  }

  async downloadGuidePages(guideId: string, fileDir: string, mimeType?: string) {
    const taskId = this.generateTaskId(`guide-${guideId}`);
    await this.controlBGMode(true, taskId);

    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;

    if (isPlatform('ios') || isPlatform('android')) {
      const pagesOptions = {
        url: `${process.env.VUE_APP_API_EXT_URL}api/google/guide/pages-with-links/${guideId}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: this.HTTP_TIMEOUT,
      };

      try {
        const response = await CapacitorHttp.get(pagesOptions);
        
        if (response.data.links) {
          try {
            await this.storeFile(
              btoa(JSON.stringify(JSON.parse(response.data.links))),
              this.GUIDE_LINKS_PATH,
              fileDir
            );
          } catch (ex) {
            console.log("Error downloading links", ex);
          }
        }
        
        const guideNumPages = Number(response.data.pages);
        if (this.entityProgressBar) {
          this.entityProgressBar.maxValue = guideNumPages;
        }
        
        const downloadPromises = [];
        for (let i = 1; i <= guideNumPages; i++) {
          downloadPromises.push(this.downloadGuidePage(guideId, i, fileDir, mimeType, guideNumPages));
        }
        
        await Promise.all(downloadPromises);
        
        // Emit downloaded event after all pages are downloaded and written to disk
        // This ensures all files are saved before setting offline flag
        if (typeof this.emitter !== 'undefined' && this.entityProgressBar) {
          // Wait a bit to ensure all files are fully written
          await new Promise(resolve => setTimeout(resolve, 500));
          this.emitter.emit(`downloaded-${this.entityProgressBar.id}`, true);
          this.clearProgressBar();
        }
        
        await this.controlBGMode(false, taskId);
        
      } catch (error) {
        console.error('Error downloading guide pages:', error);
        this.clearProgressBar();
        await this.controlBGMode(false, taskId);
      }
    }
  }

  async downloadServerGuidePages(releaseId: number, fileDir: string) {
    const taskId = this.generateTaskId(`server-guide-${releaseId}`);
    await this.controlBGMode(true, taskId);

    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;

    if (isPlatform('ios') || isPlatform('android')) {
      const pagesOptions = {
        url: `${process.env.VUE_APP_API_URL}api/releases/${releaseId}/guide/pages`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: this.HTTP_TIMEOUT,
      };

      try {
        const response = await CapacitorHttp.get(pagesOptions);
        
        const guideNumPages = Number(response.data.pages);
        if (this.entityProgressBar) {
          this.entityProgressBar.maxValue = guideNumPages;
        }
        
        const downloadPromises = [];
        for (let i = 1; i <= guideNumPages; i++) {
          downloadPromises.push(this.downloadServerGuidePage(releaseId, i, fileDir));
        }
        
        await Promise.all(downloadPromises);
        
        // Emit downloaded event after all pages are downloaded and written to disk
        if (typeof this.emitter !== 'undefined' && this.entityProgressBar) {
          // Wait a bit to ensure all files are fully written
          await new Promise(resolve => setTimeout(resolve, 500));
          this.emitter.emit(`downloaded-${this.entityProgressBar.id}`, true);
          this.clearProgressBar();
        }
        
        await this.controlBGMode(false, taskId);
        
      } catch (error) {
        console.error('Error downloading server guide pages:', error);
        this.clearProgressBar();
        await this.controlBGMode(false, taskId);
      }
    }
  }

  private async downloadServerGuidePage(releaseId: number, pageNum: number, fileDir: string): Promise<void> {
    const pageTaskId = this.generateTaskId(`server-guide-page-${releaseId}-${pageNum}`);
    await this.controlBGMode(true, pageTaskId);

    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;

    try {
      const pageResponse = await CapacitorHttp.get({
        url: `${process.env.VUE_APP_API_URL}api/releases/${releaseId}/guide/pages/base64/${pageNum > 9 ? pageNum : '0' + pageNum}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: this.HTTP_TIMEOUT,
      });

      await this.storeFile(
        pageResponse.data, 
        `guide-${pageNum > 9 ? pageNum : '0' + pageNum}`, 
        fileDir, 
        'image/png'
      );

      if (this.entityProgressBar) {
        this.entityProgressBar.currentValue = pageNum;
      }

      await this.controlBGMode(false, pageTaskId);
    } catch (error) {
      console.error(`Error downloading server guide page ${pageNum}:`, error);
      await this.controlBGMode(false, pageTaskId);
      throw error;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async downloadGuidePage(guideId: string, pageNum: number, fileDir: string, mimeType?: string, _totalPages?: number): Promise<void> {
    const pageTaskId = this.generateTaskId(`guide-page-${guideId}-${pageNum}`);
    await this.controlBGMode(true, pageTaskId);

    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;

    try {
      const pageResponse = await CapacitorHttp.get({
        url: `${process.env.VUE_APP_API_URL}api/google/guide/pages/base64/${guideId}/${pageNum > 9 ? pageNum : '0' + pageNum}`,
        headers: { Authorization: `Bearer ${token}` },
        readTimeout: this.HTTP_TIMEOUT,
      });

      await this.storeFile(
        pageResponse.data, 
        `${guideId}-${pageNum > 9 ? pageNum : '0' + pageNum}`, 
        fileDir, 
        mimeType
      );

      if (this.entityProgressBar) {
        this.entityProgressBar.currentValue = pageNum;
        // Don't emit event here - it will be emitted after all pages are downloaded
        // in downloadGuidePages method
      }

      await this.controlBGMode(false, pageTaskId);
    } catch (error) {
      console.error(`Error downloading guide page ${pageNum}:`, error);
      await this.controlBGMode(false, pageTaskId);
      throw error;
    }
  }

  async downloadFileFull(fileId: string, fileDir: string, mimeType?: string) {
    const taskId = this.generateTaskId(`download-full-${fileId}`);
    await this.controlBGMode(true, taskId);

    const tokenQuery = await Preferences.get({ key: 'jwt' });
    const token = tokenQuery.value;

    if (isPlatform('ios') || isPlatform('android')) {
      try {
        const response = await CapacitorHttp.get({
          url: `${process.env.VUE_APP_API_URL}api/google/file/base64/full/id/${fileId}`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: this.HTTP_TIMEOUT,
        });

        await this.storeFile(response.data, fileId, fileDir, mimeType);
        
        if (this.entityProgressBar) {
          this.entityProgressBar.currentValue = this.entityProgressBar.maxValue;
        }
        
        this.clearProgressBar();
        await this.controlBGMode(false, taskId);
      } catch (err) {
        console.error('downloadFileFull error: ' + err);
        this.clearProgressBar();
        await this.controlBGMode(false, taskId);
      }
    }
  }

  async downloadRelease(entityType: string) {
    if (typeof this.release === 'undefined') return;

    if (isPlatform('ios') || isPlatform('android')) {
      const taskId = this.generateTaskId(`release-${entityType}`);
      await this.controlBGMode(true, taskId);

      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;

      const srcDirectory = () => {
        if ('music' == entityType) {
          return this.releaseMusicDirectory || '';
        } else if ('video' == entityType) {
          return this.releaseVideoDirectory || '';
        } else if ('guide' == entityType) {
          return this.releaseGuideDirectory || '';
        } else {
          return '';
        }
      };

      const targetDirectory = () => {
        if ('music' == entityType) {
          return `${this.PLAYLISTS_DIRECTORY}/${this.releaseMusicDirectory}` || '';
        } else if ('video' == entityType) {
          return this.releaseVideoDirectory || '';
        } else if ('guide' == entityType) {
          return this.releaseGuideDirectory || '';
        } else {
          return '';
        }
      };

      this.initProgressBar();

      try {
        let response;
        
        // Check if release uses server storage
        if (this.contentSource === 'SERVER' && this.releaseId) {
          // Use server API
          const endpoint = entityType === 'music' 
            ? `/api/releases/${this.releaseId}/media/music`
            : entityType === 'video'
            ? `/api/releases/${this.releaseId}/media/video`
            : `/api/releases/${this.releaseId}/media/guide`;

          const options = {
            url: `${process.env.VUE_APP_API_URL}${endpoint}`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: this.HTTP_TIMEOUT,
          };

          response = await CapacitorHttp.get(options);
        } else {
          // Use Google Drive API (original logic)
          const options = {
            url: `${process.env.VUE_APP_API_URL}api/google/media/${srcDirectory()}`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: this.HTTP_TIMEOUT,
          };

          response = await CapacitorHttp.get(options);
        }
        
        if ('music' == entityType) {
          const musicList = response.data;
          if (this.entityProgressBar) {
            this.entityProgressBar.maxValue = musicList.length;
          }
          await this.storeReleaseEntity(musicList, srcDirectory(), targetDirectory());
        } else if ('video' == entityType) {
          const videoList = response.data;
          if (this.entityProgressBar) {
            this.entityProgressBar.maxValue = videoList.length;
          }
          await this.storeReleaseEntity(videoList, srcDirectory(), targetDirectory());
        } else if ('guide' == entityType) {
          const guideList = response.data;
          if (this.entityProgressBar) {
            this.entityProgressBar.maxValue = 1;
          }
          
          // For server storage, download guide pages differently
          if (this.contentSource === 'SERVER' && this.releaseId) {
            await this.downloadServerGuidePages(this.releaseId, targetDirectory());
          } else {
            // Original Google Drive guide download
            await this.downloadGuidePages(guideList[0].id, targetDirectory(), 'image/png');
          }
        }
      } catch (error) {
        console.error('Error downloading release:', error);
        this.clearProgressBar();
        await this.controlBGMode(false, taskId);
        throw error;
      }
    } else {
      console.log('not implemented');
    }
  }

  async storeReleaseEntity(entityList: Array<GoogleMedia>, srcDirectory: string, targetDirectory: string) {
    const taskId = this.generateTaskId(`store-release`);
    await this.controlBGMode(true, taskId);

    if (typeof this.programName == 'undefined') return;
    if (typeof this.programShortName == 'undefined') return;

    // Start download tracking
    if (this.entityProgressBar) {
      this.entityProgressBar.startDownload();
      this.entityProgressBar.maxValue = entityList.length;
    }

    try {
    if (entityList.length > 0) {
        const releasePlaylist = new Playlist(
            srcDirectory,
            this.programName,
            this.programShortName
        );
        releasePlaylist.programName = this.programName;
        releasePlaylist.programShortName = this.programShortName;
        releasePlaylist.tracks = entityList;
        releasePlaylist.name = `${this.programName} ${this.release}`;
        releasePlaylist.id = srcDirectory;
        releasePlaylist.isRelease = true;

        this.playlist = releasePlaylist;

        // Create directory first
        try {
          await this.createDirectory(targetDirectory);
          
          // Add all files to download queue
          for (const entity of entityList) {
            await this.addToDownloadQueue(entity.id, targetDirectory, entity.mimeType);
          }
          
          // Wait for all downloads to complete
          await this.waitForQueueToEmpty();
          
          // Finalize playlist
          await this.finalizePlaylistDownload();
          
          await this.controlBGMode(false, taskId);
        } catch (error) {
          console.error('Failed to download release files:', error);
          
          // Cleanup partial downloads on error
          if (releasePlaylist.id) {
            await this.cleanupPartialDownloads(releasePlaylist);
          }
          
          this.clearProgressBar();
          await this.controlBGMode(false, taskId);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in storeReleaseEntity:', error);
      this.clearProgressBar();
      await this.controlBGMode(false, taskId);
    }
  }

  private async waitForQueueToEmpty(): Promise<void> {
    return new Promise((resolve) => {
      const checkQueue = () => {
        // Also resolve if download was cancelled
        if (
          (this.downloadQueue.length === 0 && this.currentDownloads.size === 0) ||
          (this.entityProgressBar && !this.entityProgressBar.downloading)
        ) {
          resolve();
        } else {
          setTimeout(checkQueue, 1000);
        }
      };
      checkQueue();
    });
  }

private async createDirectory(directory: string): Promise<void> {
    try {
        await Filesystem.mkdir({
            path: directory,
            directory: Directory.Data,
            recursive: true
        });
        console.log(`Successfully created directory: ${directory}`);
    } catch (error) {
        console.error('Failed to create directory:', error);
        throw error;
    }
}

  private async downloadFilesConcurrently(entityList: GoogleMedia[], targetDirectory: string, concurrency: number) {
    const batches = [];
    for (let i = 0; i < entityList.length; i += concurrency) {
      batches.push(entityList.slice(i, i + concurrency));
    }

    for (const batch of batches) {
      try {
        // Check for failures in the batch
        const results = await Promise.allSettled(
        batch.map(entity => 
          this.downloadFileChunked(entity.id, targetDirectory, entity.mimeType)
        )
      );
        
        const failures = results.filter(result => result.status === 'rejected');
        if (failures.length > 0) {
          console.warn(`Failed to download ${failures.length} file(s) in batch:`, failures);
        }
      } catch (error) {
        console.error('Error in batch download:', error);
        throw error;
      }
    }
  }

  async downloadPlaylist(playlist: Playlist) {
    if (playlist.id == '') return;

    // Skip playlist download on web platform
    if (isPlatform('mobileweb') || isPlatform('desktop')) {
      console.warn('Playlist download is not supported on web platform');
      return;
    }

    const taskId = this.generateTaskId(`playlist-${playlist.id}`);
    await this.controlBGMode(true, taskId);

    this.playlist = playlist;

    try {
      const playlistDirectory = await Filesystem.readdir({
        path: `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`,
        directory: Directory.Data
      });

      const foundPlaylist = playlistDirectory.files.find((f) => {
        return f.name == this.PLAYLIST_PATH;
      });

      if (typeof foundPlaylist !== 'undefined' && null != foundPlaylist) {
        this.initProgressBar();

        const res = await Filesystem.readFile({
          path: `${this.PLAYLISTS_DIRECTORY}/${playlist.id}/${this.PLAYLIST_PATH}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8
        });

        let localPlaylist: Playlist | null = null;
        if (typeof res.data === 'string') {
          try {
            // Try to parse as base64 first, then fallback to plain JSON
            if (this.isBase64(res.data)) {
              localPlaylist = JSON.parse(this.unicodeUtils.fromBase64(res.data));
            } else {
              localPlaylist = JSON.parse(res.data);
            }
          } catch (parseError) {
            console.error('Failed to parse playlist data:', parseError);
            throw new Error('Invalid playlist data format');
          }
        }
        if (!localPlaylist) throw new Error("Local playlist is null");

        if (this.entityProgressBar) {
          this.entityProgressBar.maxValue = localPlaylist.tracks.length;
        }

        const tracksToDownload = [];
        for (let i = 0; i < localPlaylist.tracks.length; i++) {
          // Ignore partial downloads (files starting with dwn_)
          const foundTrack = playlistDirectory.files.find((f) => {
            return f.name.startsWith(localPlaylist.tracks[i].id) && !f.name.startsWith('dwn_');
          });

          if (typeof foundTrack !== 'undefined' && null != foundTrack) {
            if (this.entityProgressBar) {
              this.entityProgressBar.currentValue += 1;
              if (this.entityProgressBar.currentValue >= this.entityProgressBar.maxValue) {
                this.clearProgressBar();
                if (this.emitter) {
                  this.emitter.emit(`downloaded-${this.entityProgressBar.id}`, true);
                }
                await this.controlBGMode(false, taskId);
              }
            }
            continue;
          } else {
            if (this.entityProgressBar) {
              this.entityProgressBar.downloading = true;
            }
            tracksToDownload.push(localPlaylist.tracks[i]);
          }
        }

        // Загружаем отсутствующие треки
        if (tracksToDownload.length > 0) {
          await this.downloadFilesConcurrently(tracksToDownload, `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`, 3);
        }
      }
    } catch (err) {
      console.error('Failed to download playlist', err);
      this.clearProgressBar();
      await this.controlBGMode(false, taskId);
    }
  }

  async storeFile(fileData: string, filePath: string, directory: string, mimeType?: string): Promise<boolean> {
    try {
      let hasFsPermissions = true;
      const fsPermissions = await Filesystem.checkPermissions();
      if (fsPermissions.publicStorage.toString() !== 'granted') {
        const requestFsPermissions = await Filesystem.requestPermissions();
        if (requestFsPermissions.publicStorage.toString() !== 'granted') {
          hasFsPermissions = false;
        }
      }

      if (!hasFsPermissions) {
        console.error('No filesystem permissions');
        return false;
      }

      // Create directory if it doesn't exist
      try {
        await Filesystem.mkdir({
          path: directory,
          directory: Directory.Data,
          recursive: true
        });
      } catch (mkdirError: any) {
        // Ignore "Directory exists" error - this is expected on Android
        if (!mkdirError?.message?.includes('Directory exists') && !mkdirError?.message?.includes('exists')) {
          console.warn('mkdir warning in storeFile:', mkdirError);
        }
      }

      await Filesystem.writeFile({
        path: directory + '/' + filePath + (mimeType ? '.' + this.mimeExtensions.get(mimeType) : ''),
        data: fileData,
        directory: Directory.Data,
        recursive: true
      });

      return true;
    } catch (error) {
      console.log("Failed to store file", error);
      return false;
    }
  }

  async appendFile(fileData: string, filePath: string, directory: string, mimeType?: string): Promise<void> {
    try {
        let hasFsPermissions = true;
        const fsPermissions = await Filesystem.checkPermissions();
        if (fsPermissions.publicStorage.toString() !== 'granted') {
            const requestFsPermissions = await Filesystem.requestPermissions();
            if (requestFsPermissions.publicStorage.toString() !== 'granted') {
                hasFsPermissions = false;
            }
        }

        if (!hasFsPermissions) {
            throw new Error('No filesystem permissions');
        }

        // Создаем директорию если не существует
        try {
            await Filesystem.mkdir({
                path: directory,
                directory: Directory.Data,
                recursive: true
            });
        } catch (mkdirError: any) {
            // Ignore "Directory exists" error - this is expected on Android
            if (!mkdirError?.message?.includes('Directory exists') && !mkdirError?.message?.includes('exists')) {
                console.warn('mkdir warning:', mkdirError);
            }
        }

        const fullPath = directory + '/' + filePath + (mimeType ? '.' + this.mimeExtensions.get(mimeType) : '');
        
        // Проверяем, существует ли файл перед append
        let fileExists = false;
        try {
            await Filesystem.stat({
                path: fullPath,
                directory: Directory.Data,
            });
            fileExists = true;
        } catch (error) {
            fileExists = false;
        }

        if (fileExists) {
            // Файл существует - добавляем данные
            await Filesystem.appendFile({
                path: fullPath,
                data: fileData,
                directory: Directory.Data
            });
        } else {
            // Файл не существует - создаем новый
            await Filesystem.writeFile({
                path: fullPath,
                data: fileData,
                directory: Directory.Data,
                recursive: true
            });
        }
        
        console.log(`Successfully ${fileExists ? 'appended to' : 'created'} file: ${fullPath}`);
    } catch (error) {
        console.error('Failed to append file:', error);
        throw error;
    }
}

async renameFile(filePath: string, newFilePath: string, directory: string, mimeType?: string): Promise<void> {
    try {
        const fromPath = directory + '/' + filePath + (mimeType ? '.' + this.mimeExtensions.get(mimeType) : '');
        const toPath = directory + '/' + newFilePath + (mimeType ? '.' + this.mimeExtensions.get(mimeType) : '');

        // Проверяем, что исходный файл существует
        const sourceExists = await this.checkFileExists(fromPath);
        if (!sourceExists) {
            throw new Error(`Source file not found: ${fromPath}`);
        }

        // Проверяем, что целевая директория существует
        const dirExists = await this.checkDirectoryExists(directory);
        if (!dirExists) {
            await Filesystem.mkdir({
                path: directory,
                directory: Directory.Data,
                recursive: true
            });
        }

        await Filesystem.rename({
            from: fromPath,
            to: toPath,
            directory: Directory.Data,
        });
        
        console.log(`Successfully renamed file from ${fromPath} to ${toPath}`);
    } catch (error) {
        console.error('Failed to rename file:', error);
        throw error;
    }
}

  removeReleaseEntityDirectory(entityType: string): Promise<boolean> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<boolean>(async (resolve) => {
      let targetDirectory = '';
      if ('music' == entityType) {
        targetDirectory = `${this.PLAYLISTS_DIRECTORY}/${this.releaseMusicDirectory}` || '';
      } else if ('video' == entityType) {
        targetDirectory = this.releaseVideoDirectory || '';
      } else if ('guide' == entityType) {
        targetDirectory = this.releaseGuideDirectory || '';
      }

      if ('' !== targetDirectory) {
        try {
          await Filesystem.readdir({
            path: targetDirectory,
            directory: Directory.Data,
          });

          // Delete directory directly without progress bar animation
          try {
            await Filesystem.rmdir({
                      path: targetDirectory,
                      directory: Directory.Data,
                      recursive: true,
            });
            console.log(`Successfully deleted directory: ${targetDirectory}`);
                      resolve(true);
          } catch (err) {
            console.error('Error deleting release directory:', err);
                resolve(false);
          }
        } catch {
          // Directory doesn't exist, which is fine
          resolve(true);
        }
      } else {
        resolve(true);
      }
    });
  }

  async removePlaylistDirectory(playlistId: string) {
    const targetDirectory = `${this.PLAYLISTS_DIRECTORY}/${playlistId}`;
    if ('' !== targetDirectory) {
      if (this.entityProgressBar) {
        this.entityProgressBar.maxValue = 1;
        this.entityProgressBar.currentValue = 1;
        this.entityProgressBar.downloading = true;

        const interval = setInterval(() => {
          if (this.entityProgressBar) {
            this.entityProgressBar.currentValue -= .02;
            if (this.entityProgressBar.currentValue <= 0) {
              this.entityProgressBar.currentValue = 0;
              clearInterval(interval);
            }
          } else {
            clearInterval(interval);
          }
        }, 50);
      }

      try {
        await Filesystem.rmdir({
          path: targetDirectory,
          directory: Directory.Data,
          recursive: true,
        });

        if (this.entityProgressBar) this.entityProgressBar.downloading = false;

        const tokenQuery = await Preferences.get({ key: "jwt" });
        const token = tokenQuery.value;

        await CapacitorHttp.delete({
          url: `${process.env.VUE_APP_API_URL}api/playlist`,
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", },
          data: {
            name: playlistId,
          },
        });
      } catch (error) {
        console.error('Error removing playlist directory:', error);
      }
    }
  }

  async removeDownloadedPlaylist(playlist: Playlist) {
    try {
      const response = await Filesystem.readdir({
      path: `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`,
      directory: Directory.Data,
      });
      
      const deletePromises = [];
      
      for (let i = 0; i < response.files.length; i++) {
        const file = response.files[i];
        
        // Delete both the final file and any partial downloads (starting with dwn_)
        const shouldDelete = playlist.tracks.some((t) => {
          return file.name.startsWith(t.id) || file.name.startsWith(`dwn_${t.id}`);
        });

        if (shouldDelete) {
          deletePromises.push(
          Filesystem.deleteFile({
              path: `${this.PLAYLISTS_DIRECTORY}/${playlist.id}/${file.name}`,
            directory: Directory.Data
          }).catch((err) => {
              console.log(`Failed to delete file ${file.name}`, err);
          })
          );
        }
      }
      
      await Promise.all(deletePromises);
    } catch (err) {
      console.log('Failed to read playlist', err);
    }
  }

  async cleanupPartialDownloads(playlist: Playlist) {
    try {
      // Determine the correct path based on whether it's a release or regular playlist
      let directoryPath = `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`;
      
      // For video/music/guide releases, the directory doesn't have PLAYLISTS_DIRECTORY prefix
      // EXCEPT for music which uses PLAYLISTS_DIRECTORY
      if (playlist.id && playlist.id.includes('_Video')) {
        directoryPath = playlist.id;
      } else if (playlist.id && playlist.id.includes('_Guide')) {
        directoryPath = playlist.id;
      } else if (playlist.id && playlist.id.includes('_Music')) {
        directoryPath = `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`;
      }
      
      console.log(`Cleaning up partial downloads from: ${directoryPath}`);
      
      const response = await Filesystem.readdir({
        path: directoryPath,
        directory: Directory.Data,
      });
      
      console.log(`Found ${response.files.length} files in ${directoryPath}`);
      
      const deletePromises = [];
      
      for (let i = 0; i < response.files.length; i++) {
        const file = response.files[i];
        
        // Delete only partial downloads (starting with dwn_)
        if (file.name.startsWith('dwn_')) {
          console.log(`Deleting partial file: ${file.name}`);
          deletePromises.push(
            Filesystem.deleteFile({
              path: `${directoryPath}/${file.name}`,
              directory: Directory.Data
            }).then(() => {
              console.log(`Successfully deleted: ${file.name}`);
            }).catch((err) => {
              console.error(`Failed to delete partial file ${file.name}:`, err);
            })
          );
        }
      }
      
      await Promise.all(deletePromises);
      console.log(`Cleaned up ${deletePromises.length} partial files from ${directoryPath}`);
    } catch (err) {
      console.error('Failed to cleanup partial downloads:', err);
    }
  }

  removeFromDownloadedPlaylist(playlist: Playlist, track: GoogleMedia) {
    if (playlist.id == '') return;

    Filesystem.readdir({
      path: `${this.PLAYLISTS_DIRECTORY}/${playlist.id}`,
      directory: Directory.Data,
    }).then((response) => {
      const foundFile = response.files.find((f) => {
        return f.name.startsWith(track.id);
      })

      if (typeof foundFile !== 'undefined' && null !== foundFile) {
        Filesystem.deleteFile({
          path: `${this.PLAYLISTS_DIRECTORY}/${playlist.id}/${foundFile}`,
          directory: Directory.Data
        }).catch((err) => {
          console.log('Failed to delete file', err);
        })
      }
    })
    .catch((err) => {
      console.log('Failed to read playlist', err);
    })
  }

  removeAllDownloadedPlaylist() {
    Filesystem.readdir({
      path: `${this.PLAYLISTS_DIRECTORY}`,
      directory: Directory.Data,
    }).then((response) => {
      response.files.forEach((d) => {
        Filesystem.readdir({
          path: `${this.PLAYLISTS_DIRECTORY}/${d}`,
          directory: Directory.Data,
        }).then((response) => {
          const foundPlaylist = response.files.find((f) => {
            return f.name == this.PLAYLIST_PATH;
          });

          if (typeof foundPlaylist !== 'undefined' && null != foundPlaylist) {
            Filesystem.readFile({
              path: `${this.PLAYLISTS_DIRECTORY}/${d}/${this.PLAYLIST_PATH}`,
              directory: Directory.Data,
              encoding: Encoding.UTF8
            }).then((res) => {
              const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
              if (!localPlaylist) throw new Error("Local playlist is null");
              this.removeDownloadedPlaylist(localPlaylist);
            }).catch((err) => {
              console.error('Failed to load local playlist', err);
            })
          }
        })
        .catch((err) => {
          console.log('Failed to read playlist', err);
        })
      });
    })
    .catch((err) => {
      console.log('Error', err);
    })
  }

  isPlaylistOffline(playlistDir: string, playlist: Playlist): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      Filesystem.readdir({
        path: `${playlistDir}/${playlist.id}`,
        directory: Directory.Data,
      }).then((response) => {
        let allOffline = true;
        for (let i = 0; i < playlist.tracks.length; i++) {
          // Ignore partial downloads (files starting with dwn_)
          const foundTrack = response.files.find((f) => {
            return f.name.startsWith(playlist.tracks[i].id) && !f.name.startsWith('dwn_');
          })

          if (typeof foundTrack == 'undefined' && null == foundTrack) {
            allOffline = false;
            break;
          }
        }
        resolve(allOffline);
      })
      .catch((err) => {
        console.log('Failed to read playlist', err);
        resolve(false);
      })
    });
  }

  isReleaseOffline(releaseEntity: Release, entityType?: string) {
    if (!entityType || entityType == "music") {
      Filesystem.readdir({
        path: `${this.PLAYLISTS_DIRECTORY}/${releaseEntity.musicDirectory}`,
        directory: Directory.Data,
      })
      .then((musicDirContent) => {
        const foundPlaylist = musicDirContent.files.find((f) => {
          return f.name == this.PLAYLIST_PATH;
        });

        if (typeof foundPlaylist !== "undefined" && null != foundPlaylist) {
          Filesystem.readFile({
            path: `${this.PLAYLISTS_DIRECTORY}/${releaseEntity.musicDirectory}/${this.PLAYLIST_PATH}`,
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          })
          .then((res) => {
            const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
            if (!localPlaylist) throw new Error("Local playlist is null");

            this
            .isPlaylistOffline(
              this.PLAYLISTS_DIRECTORY,
              localPlaylist
            )
            .then((response) => {
              releaseEntity.musicOffline = response;
            });
          })
          .catch((err) => {
            console.error("Failed to load local music playlist" + err);
          });
        }
      })
      .catch(() => {
        releaseEntity.musicOffline = false;
      });
    }
    if (!entityType || entityType == "video") {
      Filesystem.readdir({
        path: releaseEntity.videoDirectory,
        directory: Directory.Data,
      })
      .then((videoDirContent) => {
        const foundPlaylist = videoDirContent.files.find((f) => {
          return f.name == this.PLAYLIST_PATH;
        });

        if (typeof foundPlaylist !== "undefined" && null != foundPlaylist) {
          Filesystem.readFile({
            path: `${releaseEntity.videoDirectory}/${this.PLAYLIST_PATH}`,
            directory: Directory.Data,
            encoding: Encoding.UTF8,
          })
          .then((res) => {
            const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
            if (!localPlaylist) throw new Error("Local playlist is null");

            this
            .isPlaylistOffline("", localPlaylist)
            .then((response) => {
              releaseEntity.videoOffline = response;
            });
          })
          .catch((err) => {
            console.error("Failed to load local video playlist", err);
          });
        }
      })
      .catch(() => {
        releaseEntity.videoOffline = false;
      });
    }
    if (!entityType || entityType == "guide") {
      Filesystem.readdir({
        path: releaseEntity.guideDirectory,
        directory: Directory.Data,
      })
      .then((response) => {
        releaseEntity.guideOffline = response.files.length > 0 && response.files[0].name.endsWith("png");
      })
      .catch(() => {
        releaseEntity.guideOffline = false;
      });
    }
  }

  initProgressBar() {
    if (this.entityProgressBar) {
      this.entityProgressBar.currentValue = 0;
      this.entityProgressBar.downloaded = 0;
      this.entityProgressBar.downloading = true;
    }
  }

  clearProgressBar() {
    if (this.entityProgressBar) {
      this.entityProgressBar.currentValue = 0;
      this.entityProgressBar.maxValue = 0;
      this.entityProgressBar.downloaded = 0;
      this.entityProgressBar.downloading = false;
    }
  }

  cancelAllDownloads() {
    // Mark progress bar as not downloading to stop all chunks
    if (this.entityProgressBar) {
      this.entityProgressBar.downloading = false;
    }

    // Clear the download queue
    this.downloadQueue = [];
    
    // Mark all current downloads as cancelled
    this.currentDownloads.forEach((task) => {
      this.cancelledDownloads.add(task.fileId);
    });
    
    // Clear current downloads map
    this.currentDownloads.clear();
    
    console.log('Cancelled all downloads');
  }

  /**
   * Create or update playlist for single video downloads
   * This allows individual videos to be marked as offline
   */
  async createOrUpdateVideoPlaylist(
    videoDirectory: string,
    videoList: Array<GoogleMedia>,
    programName: string,
    programShortName: string,
    releaseWithZero: string
  ): Promise<void> {
    try {
      // Check which videos are actually downloaded
      const dirContent = await Filesystem.readdir({
        path: videoDirectory,
        directory: Directory.Data,
      });

      const downloadedVideos: Array<GoogleMedia> = [];
      for (const video of videoList) {
        const fileExtension = this.mimeExtensions.get(video.mimeType || '') || 'mp4';
        const fileName = `${video.id}.${fileExtension}`;
        
        const fileExists = dirContent.files.some(f => 
          f.name === fileName && !f.name.startsWith('dwn_') && f.size > 0
        );
        
        if (fileExists) {
          downloadedVideos.push(video);
        }
      }

      // Create or update playlist
      const playlist = new Playlist(
        videoDirectory,
        programName,
        programShortName
      );
      playlist.programName = programName;
      playlist.programShortName = programShortName;
      playlist.tracks = downloadedVideos;
      playlist.name = `${programName} ${releaseWithZero}`;
      playlist.id = videoDirectory;
      playlist.isRelease = true;
      playlist.offline = downloadedVideos.length > 0;
      playlist.offlineDate = `${dayjs().date()}.${String(dayjs().month() + 1).padStart(2, '0')}.${dayjs().year()}`;

      // Store playlist
      await this.storeFile(
        this.unicodeUtils.toBase64(JSON.stringify(playlist)),
        this.PLAYLIST_PATH,
        videoDirectory
      );

      // Sync with API
      const tokenQuery = await Preferences.get({ key: 'jwt' });
      const token = tokenQuery.value;

      if (token) {
        try {
          await CapacitorHttp.put({
            url: `${process.env.VUE_APP_API_URL}api/playlist`,
            headers: { 
              Authorization: `Bearer ${token}`, 
              "Content-Type": "application/json",
            },
            data: {
              name: playlist.id,
              content: JSON.stringify(playlist),
            },
          });
        } catch (apiError) {
          console.warn('Failed to sync playlist with API (non-critical):', apiError);
        }
      }
    } catch (error) {
      console.error('Failed to create/update video playlist:', error);
      throw error;
    }
  }
}